import React, { useMemo } from "react";
import { Box, Typography, Tooltip, useTheme } from "@mui/material";

const DAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"]; // index+1 matches Mongo's $dayOfWeek
const HOUR_LABELS = [0, 3, 6, 9, 12, 15, 18, 21]; // sparse axis labels — 24 of them would be unreadable

/** Weekly day x hour call-volume heatmap. Pure CSS grid, no charting library needed for this shape. */
export default function CallsHeatmap({ data }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const { grid, max } = useMemo(() => {
    const g = Array.from({ length: 7 }, () => Array(24).fill(0));
    let m = 0;
    (data || []).forEach(({ dayOfWeek, hour, count }) => {
      const row = dayOfWeek - 1; // Mongo's $dayOfWeek is 1-indexed (1=Sunday)
      if (row >= 0 && row < 7 && hour >= 0 && hour < 24) {
        g[row][hour] = count;
        if (count > m) m = count;
      }
    });
    return { grid: g, max: m };
  }, [data]);

  const cellColor = (count) => {
    if (count === 0) return isDark ? "#1e2521" : "#eceee8";
    const intensity = max > 0 ? count / max : 0;
    // Teal ramp, consistent with the rest of the calling UI's accent color.
    const alpha = 0.15 + intensity * 0.75;
    return isDark ? `rgba(82, 213, 186, ${alpha})` : `rgba(31, 122, 108, ${alpha})`;
  };

  return (
    <Box>
      <Box sx={{ display: "flex", gap: "3px" }}>
        <Box sx={{ width: 32 }} />
        <Box sx={{ flex: 1, display: "grid", gridTemplateColumns: "repeat(24, 1fr)", gap: "2px" }}>
          {Array.from({ length: 24 }).map((_, h) => (
            <Typography key={h} sx={{ fontSize: 9, color: "text.secondary", textAlign: "center" }}>
              {HOUR_LABELS.includes(h) ? h : ""}
            </Typography>
          ))}
        </Box>
      </Box>
      {grid.map((row, dayIdx) => (
        <Box key={dayIdx} sx={{ display: "flex", gap: "3px", mb: "3px" }}>
          <Typography sx={{ width: 32, fontSize: 11, color: "text.secondary", lineHeight: "18px" }}>
            {DAY_LABELS[dayIdx]}
          </Typography>
          <Box sx={{ flex: 1, display: "grid", gridTemplateColumns: "repeat(24, 1fr)", gap: "2px" }}>
            {row.map((count, hour) => (
              <Tooltip key={hour} title={`${DAY_LABELS[dayIdx]} ${hour}:00 — ${count} call${count === 1 ? "" : "s"}`}>
                <Box sx={{ height: 18, borderRadius: "2px", bgcolor: cellColor(count) }} />
              </Tooltip>
            ))}
          </Box>
        </Box>
      ))}
    </Box>
  );
}
